/**
 * Shared input-validation helpers used across parent-facing API
 * routes. Extracted during the §sec sprint so every route uses the
 * same UUID shape check instead of rolling its own (or forgetting
 * to — missing validation on plans / plan-entries / plan-share-links
 * was the audit finding that prompted this module).
 *
 * These helpers only validate SHAPE. Ownership / authorization is
 * still enforced by RLS; this module is an early-exit reject so a
 * bad body never reaches the database layer and so error responses
 * can be specific ("Invalid plan_id.") instead of PostgREST-shaped
 * ("new row violates row-level security policy").
 */

// Canonical UUID v4/v5 pattern. Case-insensitive so either hex
// casing is accepted. Matches the Postgres uuid type's accepted
// shape.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True if `v` is a string that matches the canonical UUID shape. */
export function isUuid(v: unknown): v is string {
  return typeof v === "string" && UUID_RE.test(v);
}

/**
 * Narrowing helper for body parsing: returns `v` if it's a UUID
 * string, `null` otherwise. Use in request body destructuring so
 * the route handler can do `if (!planId) return 400` without a
 * separate typeof check.
 */
export function pickUuid(v: unknown): string | null {
  return isUuid(v) ? v : null;
}

/**
 * yyyy-mm-dd date shape check. Doesn't validate that the date is
 * real (Postgres will bounce Feb 30), just that the string shape is
 * what the database expects so we don't forward nonsense.
 */
export function isIsoDate(v: unknown): v is string {
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);
}

/**
 * Narrow a raw URL string to one that's safe to drop into an
 * `<a href>`. Returns the original string if and only if it parses
 * as an absolute http:// or https:// URL, `null` otherwise.
 *
 * This exists to neutralize `javascript:`, `data:`, `vbscript:`,
 * `file:`, and other script-capable protocols that a compromised or
 * sloppy admin could save into `camps.registration_url` or
 * `providers.website_url` via the admin tool. Those URLs are shown
 * on the public camp detail page, so a malicious `javascript:…`
 * value would execute in every visitor's browser.
 *
 * Accepts URLs with any host, including ones that redirect
 * externally — that's by design, the Finder deliberately links out
 * to provider registration pages. The only check is protocol.
 */
export function safeHttpUrl(v: unknown): string | null {
  if (typeof v !== "string" || v.length === 0) return null;
  // URL() is available in both Node and the browser. Relative URLs
  // (no protocol) throw without a base, which is fine — we reject
  // anything that isn't an absolute http(s) URL.
  try {
    const u = new URL(v);
    if (u.protocol === "http:" || u.protocol === "https:") return v;
    return null;
  } catch {
    return null;
  }
}

/**
 * Hosts allowed to be loaded inside a video `<iframe src>`. Kept to
 * the YouTube / Vimeo embed endpoints we actually use.
 */
const EMBED_HOSTS = new Set([
  "www.youtube.com",
  "youtube.com",
  "www.youtube-nocookie.com",
  "youtube-nocookie.com",
  "player.vimeo.com",
]);

/**
 * Narrow a raw URL to one safe to drop into an `<iframe src>`. Unlike
 * `safeHttpUrl` (which accepts any host by design — we deliberately
 * link out to provider pages), an iframe *frames* the target origin's
 * content, so an attacker-controlled `https://evil.com/…` host is
 * still a clickjacking / frame-injection vector even though it passes
 * the protocol gate. We therefore additionally require the URL to be a
 * known video-embed endpoint (allowlisted host + `/embed/` or
 * `/video/` path).
 *
 * Returns the validated URL string, or `null` if it is not an http(s)
 * URL on an allowlisted embed host + path. Protocol validation is
 * delegated to `safeHttpUrl` so the rejection of `javascript:` /
 * `data:` / etc. lives in exactly one place.
 */
export function safeEmbedUrl(v: unknown): string | null {
  const safe = safeHttpUrl(v); // protocol gate first; reuse, don't reimplement
  if (!safe) return null;
  let u: URL;
  try {
    u = new URL(safe);
  } catch {
    return null;
  }
  const host = u.hostname.toLowerCase();
  if (host === "player.vimeo.com") {
    // https://player.vimeo.com/video/<id>
    return u.pathname.startsWith("/video/") ? safe : null;
  }
  if (EMBED_HOSTS.has(host)) {
    // youtube embeds live under /embed/<id>
    return u.pathname.startsWith("/embed/") ? safe : null;
  }
  return null;
}
